import { node, text } from "~/util/dom";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { store } from "~/state/store";
import { v4 as uuidv4 } from "uuid";
import { ELoginState } from "~/state/user/interfaces";

interface IDeepLData {
	text?: string;
	lang: string;
	container: HTMLElement;
}

interface ITranslateData {
	posible: boolean;
	translatedText?: string;
}

interface IResponse {
	code: number;
	message: string;
	text?: string;
}

export function createDOMElements(native = true) {
	const container = node("p", { className: "deepl" });
	const button = node("button", { className: "deepl-btn" }, native ? _("deepl.button") : _("deepl.original"));
	const origButton = node("button", {}, _("deepl.original"));
	const translateButton = node("button", {}, _("deepl.translate"));

	button.setAttribute("data-log-ignore", "1");
	origButton.setAttribute("data-log-ignore", "1");
	translateButton.setAttribute("data-log-ignore", "1");

	return {
		container,
		button,
		origButton,
		translateButton,
	};
}

function callRequest(txt: string) {
	const REQUEST_URL = `${CONFIG.requestUrl}/translate`;

	return fetch(REQUEST_URL, {
		headers: {
			"Accept": "application/json",
			"Content-Type": "application/json",
			"X-Correlation-Id": uuidv4(),
		},
		credentials: "include",
		method: "POST",
		body: JSON.stringify({
			text: txt,
			lang: Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang,
		}),
	});
}

function translateTexts(texts: Array<string> = []): Array<Promise<IResponse>> {
	const promiseArray = [];

	texts.forEach(txt => {
		if (txt) {
			// eslint-disable-next-line no-async-promise-executor
			promiseArray.push(new Promise(async (resolve, reject) => {
				const response = await callRequest(txt);

				if (response.status === 200) {
					const json = await response.json();

					json ? resolve(json) : reject();
				} else {
					reject();
				}

				resolve("");
			}));
		} else {
			promiseArray.push(Promise.resolve(""));
		}
	});

	return promiseArray;
}

function switchTranslate(data: Array<IDeepLData>, translateData: Array<ITranslateData>, elements: {
	origButton: HTMLButtonElement,
	translateButton: HTMLButtonElement,
	container: HTMLParagraphElement,
 }, orig?: boolean) {
	data.forEach((item, index) => {
		if (item.container) {
			item.container.innerHTML = orig || !translateData[index].posible || !translateData[index].translatedText ? item.text : translateData[index].translatedText;
		}
	});

	elements.origButton.classList[orig ? "add" : "remove"]("nonactive");
	elements.translateButton.classList[orig ? "remove" : "add"]("nonactive");
}

export function createDeepLTranslate(data: Array<IDeepLData> | IDeepLData, type = "", changeCb?: (translate?: boolean) => void): HTMLParagraphElement {
	const { user } = store.getState().user;

	if (user.state === ELoginState.Green) {
		const { container, button, origButton, translateButton } = createDOMElements();
		const translateData: Array<ITranslateData> = [];
		const arrayData = Array.isArray(data) ? data : [data];
		let showButton = false;

		arrayData.forEach(item => {
			if (!showButton) {
				showButton = item.lang !== (Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang);
			}

			translateData.push({
				posible: item.lang && item.lang !== (Mapy.CUS.userweb.deeplLang || CONFIG.defaultLang),
			});
		});

		if (showButton) {
			button.addEventListener("click", () => {
				const loading = node("span", {}, _("deepl.loading"));

				button.remove();
				container.appendChild(loading);

				const textsTrans = translateData.map((item, index) => item.posible && arrayData[index].text ? arrayData[index].text : "");
				let haveTranslateText = false;

				Promise.allSettled(translateTexts(textsTrans)).then(translatedText => {
					translatedText.forEach((trans, index) => {
						if (trans.status === "fulfilled" && trans.value?.text) {
							translateData[index].translatedText = trans.value.text;
							haveTranslateText = true;
						}

						switchTranslate(arrayData, translateData, {
							origButton,
							translateButton,
							container,
						});
					});

					container.innerHTML = "";

					if (haveTranslateText) {
						container.appendChild(translateButton);
						container.appendChild(text(" · "));
						container.appendChild(origButton);

						origButton.addEventListener("click", () => {
							!origButton.classList.contains("nonactive") && switchTranslate(arrayData, translateData, {
								origButton,
								translateButton,
								container,
							}, true);

							changeCb && changeCb();
						}, false);

						translateButton.addEventListener("click", () => {
							!translateButton.classList.contains("nonactive") && switchTranslate(arrayData, translateData, {
								origButton,
								translateButton,
								container,
							});

							changeCb && changeCb(true);
						}, false);

						changeCb && changeCb(true);
					} else {
						container.appendChild(button);
						container.appendChild(text(" · "));
						container.appendChild(node("i", {}, _("deepl.error")));
					}

					Mapy.getComponent("logger").log("translate", {
						type,
					});
				});
			}, false);

			container.appendChild(button);

			return container;
		}
	}

	return null;
}
